import { horas, dias } from './datas'

/**
 * Normaliza los campos de sede de una clase para soportar multi-sede.
 * Clases viejas tienen solo `sede`/`nombre_sede`, las nuevas tienen `sedes`/`nombres_sedes`.
 * @param {Object} classData - Datos de la clase
 * @returns {{ sedes: string[], nombres_sedes: string[] }}
 */
export const normalizeClassSedes = (classData) => {
    const sedes = classData.sedes || (classData.sede ? [classData.sede] : [])
    const nombres_sedes = classData.nombres_sedes || (classData.nombre_sede ? [classData.nombre_sede] : [])
    return { sedes, nombres_sedes }
}

/**
 * Filtra profesores por las sedes seleccionadas de una clase.
 * - Si el profesor no tiene `sedes_profesor[academia]`, se incluye (backward compat)
 * - Si no hay sedes seleccionadas, se muestran todos
 * @param {Array} teachers - Lista de profesores
 * @param {string} academia - Email/ID de la academia
 * @param {string[]} selectedSedeIds - IDs de sedes seleccionadas
 * @returns {Array} - Profesores filtrados
 */
export const filterTeachersBySedes = (teachers, academia, selectedSedeIds) => {
    if (!selectedSedeIds || selectedSedeIds.length === 0) return teachers
    return teachers.filter(t => {
        const profSedes = t.sedes_profesor?.[academia]
        if (!profSedes || profSedes.length === 0) return true
        return selectedSedeIds.some(sedeId => profSedes.includes(sedeId))
    })
}

/**
 * Ordena una clase en el bucket del dia de la semana correspondiente.
 * @param {Object} clase - Objeto de clase con dias_clase[]
 * @param {Array} sortedDaysData - Array de 7 arrays (uno por dia de la semana)
 */
export const sortByDay = (clase, sortedDaysData) => {
    for(let i = 0; i < dias.length; i++){
        let found = false
        for(let j = 0; j < clase.dias_clase.length; j++){
            if(clase.dias_clase[j].dia === dias[i]){
                sortedDaysData[i].push(clase)
                found = true
                break
            }
        }
        if(found) break
    }
}

/**
 * Toma un array de clases ya separadas por dia y las ordena por hora de inicio.
 * @param {Array} sortedDaysData - Array de 7 arrays con clases separadas por dia
 * @returns {Array} - Lista plana de clases ordenadas por dia y hora
 */
export const sortAndOrderClasses = (sortedDaysData) => {
    var result = []

    for(let i = 0; i < sortedDaysData.length; i++){
        var h = horas.map(() => [])

        for(let x = 0; x < sortedDaysData[i].length; x++){
            var start = []

            for(let z = 0; z < sortedDaysData[i][x].dias_clase.length; z++){
                start.push(sortedDaysData[i][x].dias_clase[z].de)
            }

            for(let z = 0; z < h.length; z++){
                if(start.includes(horas[z])){
                    h[z].push(sortedDaysData[i][x])
                    break
                }
            }
        }

        h.forEach(slot => {
            slot.forEach(clase => {
                result.push(clase)
            })
        })
    }

    return result
}

/**
 * Crea un array vacio de 7 arrays (uno por dia de la semana).
 */
export const createEmptySortedDays = () => [[], [], [], [], [], [], []]

/**
 * Filtra y ordena clases de una academia/sede, aplicando:
 * - Filtro de IDs validos (sin guion = clase principal)
 * - Filtro de capacidad
 * - Ordenamiento por dia y hora
 * @param {Array} clasesDocs - Documentos de Firestore de clases
 * @param {string} academiaEmail - Email de la academia
 * @param {Object} extraData - Datos extra para agregar a cada clase (ej: sede info)
 * @returns {Array} - Clases filtradas y ordenadas
 */
export const filterAndSortClasses = (clasesDocs, academiaEmail, extraData = {}) => {
    var filtered = []

    clasesDocs.forEach((v) => {
        var d = v.data()

        if(v.id.split("-").length === 1){
            if(d.limite_jugadores > 0){
                if((d.numero_jugadores + d.clases_muestra) < parseInt(d.limite_jugadores)){
                    filtered.push({
                        id: v.id,
                        ref: v.ref.path,
                        academia: academiaEmail,
                        ...d,
                        ...extraData
                    })
                }
            } else {
                filtered.push({
                    id: v.id,
                    ref: v.ref.path,
                    academia: academiaEmail,
                    ...d,
                    ...extraData
                })
            }
        }
    })

    // Ordenar por dia y hora
    var sortedDays = createEmptySortedDays()
    for(let i = 0; i < filtered.length; i++){
        sortByDay(filtered[i], sortedDays)
    }

    return sortAndOrderClasses(sortedDays)
}

/**
 * Formatea el subtitulo de una clase con sus dias y horas.
 * @param {Array} dias_clase - Array de {dia, de, a}
 * @returns {string} - Texto formateado ej: "Lun 08:00-09:00 / Mie 08:00-09:00"
 */
export const formatSubtitle = (dias_clase) => {
    var result = ""
    dias_clase.forEach(v => {
        var dia = v.dia.substring(0, 3)
        var hora = v.de + "-" + v.a

        if (result === "") {
            result = dia + " " + hora
        } else {
            result += " / " + dia + " " + hora
        }
    })
    return result
}
